// NEW PROJECT - Editor Component
// File: /home/user/captis-editor/src/components/Editor/index.tsx

"use client";

import React, { useEffect, useState, useRef } from "react";
import { EditorContent, Editor as TipTapEditor } from "@tiptap/react";
import classNames from "classnames";
import { isEqual } from "lodash";

import { useEditor, EditorStores, CustomModules } from "../../hooks/useEditor";
import { EditorProps } from "../../types/editor";
import styles from "./Editor.module.css";

// Update the EditorProps interface to include the new required props
interface RefactoredEditorProps extends Omit<EditorProps, "customObject"> {
    value?: any;
    onChange?: (editor: TipTapEditor) => void;
    onEditorReady?: (editor: TipTapEditor) => void;
    dictionary?: any;
    abbreviation?: any;
    metadata?: {
        sectionId?: string;
        clientId?: string;
        [key: string]: any;
    };
    readonly?: boolean;
    project?: any;
    templateMode?: boolean;
    historyMode?: boolean;

    // New required props for the refactored implementation
    stores: EditorStores;
    modules: CustomModules;

    // Optional custom object plugin component
    CustomObjectPlugin?: React.ComponentType<{
        editor: TipTapEditor;
        config?: any;
    }>;

    // Custom object configuration
    customObjectConfig?: any;

    // Additional props that might be passed through
    [key: string]: any;
}

const Editor: React.FC<RefactoredEditorProps> = ({
    value,
    onChange,
    onEditorReady,
    dictionary,
    abbreviation,
    metadata = {},
    readonly = false,
    project,
    templateMode = false,
    historyMode = false,
    stores,
    modules,
    CustomObjectPlugin,
    customObjectConfig,
    ...rest
}) => {
    // const { sectionId, clientId } = metadata;
    const [isReady, setReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const isUpdatingContent = useRef(false);

    /**
     * Define the debounced function to handle update events
     */
    const onUpdate = (editor: TipTapEditor) => {
        if (onChange && !isUpdatingContent.current) {
            onChange(editor);
        }
    };

    /**
     * Configure the editor using the new props-based approach
     */
    const editor = useEditor({
        content: value,
        onUpdate: ({ editor }) => {
            onUpdate(editor);
        },
        editorProps: {
            attributes: {
                class: styles.content,
            },
            handleKeyDown: (view: any, event: KeyboardEvent) => {
                // Handle special key combinations here
                return false;
            },
        },
        metadata,
        readonly,
        templateMode,
        stores,
        modules,
    });

    const focusEditor = () => {
        if (editor && !readonly) {
            // Trying to address a race condition in which the editor is
            // destroyed before we focus it
            setTimeout(() => {
                if (!editor.isDestroyed) {
                    editor.commands.focus("start", { scrollIntoView: true });
                }
            }, 10);

            const { selection } = editor.state;
            // Check if selection is on an image node
            if ((selection as any)?.node?.type?.name === "image") {
                editor.commands.createParagraphNear();
            }
        }
    };

    // Listen to content changes and update the editor state
    useEffect(() => {
        if (
            isReady &&
            editor &&
            value?.type &&
            !isEqual(editor.getJSON(), value) &&
            !isUpdatingContent.current
        ) {
            isUpdatingContent.current = true;
            editor.commands.setContent(value);
            // Reset the flag after a short delay to allow the editor to process the change
            setTimeout(() => {
                isUpdatingContent.current = false;
            }, 0);
        }

        // focusEditor();
    }, [value, editor, isReady]);

    useEffect(() => {
        if (editor) {
            setReady(true);
            if (onEditorReady) {
                onEditorReady(editor);
            }
        }
    }, [editor, onEditorReady]);

    if (!editor) {
        return (
            <div className={styles.editor}>
                <div className={styles.loading}>
                    <div className={styles.loadingSpinner} />
                </div>
            </div>
        );
    }

    const hideHelpText = () => {
        if (templateMode) {
            return false;
        }

        if (readonly) {
            return true;
        }

        // Check if help text store is available from modules
        if (
            editor.storage &&
            modules?.ReportDocumentSection?.HELP_TEXT?.MARK_NAME
        ) {
            const markName = modules.ReportDocumentSection.HELP_TEXT.MARK_NAME;
            return !editor.storage[markName]?.store?.isVisible;
        }

        // For now, always show help text if store is not available
        return false;
    };

    return (
        <div
            className={classNames(styles.editor, {
                [styles.readonly]: readonly,
                [styles.hideHelpText]: hideHelpText(),
            })}
        >
            <EditorContent editor={editor} />

            {/* Render CustomObjectPlugin if provided */}
            {editor && CustomObjectPlugin && (
                <CustomObjectPlugin
                    editor={editor}
                    config={customObjectConfig}
                />
            )}

            {isLoading && (
                <div className={styles.loading}>
                    <div className={styles.loadingSpinner} />
                </div>
            )}
        </div>
    );
};

Editor.displayName = "Editor";

export default Editor;

// Export the props type for consumers
export type { RefactoredEditorProps as EditorComponentProps };
